import React, { useRef, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  MDXEditor,
  jsxPlugin,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  linkPlugin,
  toolbarPlugin,
  tablePlugin,
  markdownShortcutPlugin,
  frontmatterPlugin,
  thematicBreakPlugin,
  KitchenSinkToolbar,
  directivesPlugin,
} from "@mdxeditor/editor";

import { EditableTableTextProps } from "./types";
import Button from "../Button";
import Check from "../../assets/icons/Check.svg";
import XClose from "../../assets/icons/x-close.svg";
import { ButtonWrapper, EditableTableTextContainer, TextWrapper } from "./styles";
import TextInput from "../TextInput";

const EditableTableText: React.FC<EditableTableTextProps> = ({
  defaultValue,
  onChange,
  onSubmitChange,
  onClick,
  markdown,
  notEditable,
  marginLeft,
  $alwaysBorder,
  $grow,
  $noPadding,
  placeholder,
  $clearOnSubmit,
  maxWidth,
  minWidth,
  textColor,
  icon
}) => {
  const [valueBeforeEdit, setValueBeforeEdit] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [internalValue, setInternalValue] = useState<string>(
    defaultValue || ""
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleChange = (newValue: string) => {
    if (newValue.length === 0) {
      setIsError(true);
    } else {
      setIsError(false);
    }

    if (newValue.length > 0) {
      setInternalValue(newValue);
      onChange?.(newValue);
    }
  };

  const handleCommenceEdit = () => {
    setValueBeforeEdit(internalValue);
    setIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleSubmitChange = () => {
    if (isError) return;
    setIsEditing(false);
    onSubmitChange(internalValue);
    if ($clearOnSubmit) {
      setInternalValue("");
    }
  };

  const handleDeleteChange = () => {
    setInternalValue(valueBeforeEdit);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleDeleteChange();
    }
    if (e.key === "Enter") {
    e.preventDefault();
    handleSubmitChange();
    }
  };

  useEffect(() => {
    if (!isEditing) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsEditing(false);
        setInternalValue(valueBeforeEdit);
        setIsError(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing, valueBeforeEdit]);

  return (
    <EditableTableTextContainer
      ref={containerRef}
      isEditing={isEditing}
      isError={isError}
      marginLeft={marginLeft}
      $alwaysBorder={$alwaysBorder}
      $grow={$grow}
      onClick={() => {
        if (!isEditing && !notEditable) {
          handleCommenceEdit();
        }
        onClick && onClick();
      }}
      maxWidth={maxWidth}
      minWidth={minWidth}
    >
      <TextWrapper textColor={textColor} title={internalValue} data-fulltext={internalValue}>
        {!markdown && (
          <TextInput
            initialValue={internalValue}
            $borderless
            hideClear
            icon={icon}
            disabled={!isEditing}
            $grow={!$noPadding}
            $noPadding={$noPadding}
            onChangeCallback={handleChange}
            onClick={onClick}
            placeholder={placeholder || "Please enter a value"}
            onKeyDown={handleKeyDown}
          />
        )}
        {markdown && (
          <>
            {!isEditing && (
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => (
                    <h1
                      style={{ fontSize: "2em", fontWeight: "bold" }}
                      {...props}
                    />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2
                      style={{ fontSize: "1.7em", fontWeight: "bold" }}
                      {...props}
                    />
                  ),
                  h3: ({ node, ...props }) => (
                    <h2
                      style={{ fontSize: "1.4em", fontWeight: "bold" }}
                      {...props}
                    />
                  ),
                  h4: ({ node, ...props }) => (
                    <h2
                      style={{ fontSize: "1.2em", fontWeight: "bold" }}
                      {...props}
                    />
                  ),
                  strong: ({ node, ...props }) => (
                    <strong style={{ fontWeight: "bold" }} {...props} />
                  ),
                  table: ({ node, ...props }) => (
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        marginBottom: "1em",
                      }}
                      {...props}
                    />
                  ),
                  thead: ({ node, ...props }) => <thead {...props} />,
                  th: ({ node, ...props }) => (
                    <th
                      style={{
                        textAlign: "left",
                        fontWeight: "bold",
                        padding: "8px",
                        borderBottom: "2px solid #ddd",
                      }}
                      {...props}
                    />
                  ),
                  td: ({ node, ...props }) => (
                    <td
                      style={{ padding: "8px", borderBottom: "1px solid #ddd" }}
                      {...props}
                    />
                  ),
                }}
                remarkPlugins={[remarkGfm]}
              >
                {internalValue}
              </ReactMarkdown>
            )}
            {isEditing && (
              <div tabIndex={0} onKeyDown={handleKeyDown}>
                <MDXEditor
                  markdown={internalValue}
                  plugins={[
                    headingsPlugin(),
                    listsPlugin(),
                    quotePlugin(),
                    linkPlugin(),
                    tablePlugin(),
                    directivesPlugin(),
                    jsxPlugin(),
                    thematicBreakPlugin(),
                    markdownShortcutPlugin(),
                    frontmatterPlugin(),
                  ]}
                  onChange={handleChange}
                />
              </div>
            )}
          </>
        )}
        {isEditing && (
        <ButtonWrapper>
          <Button
            iconLeft={XClose}
            handleClick={handleDeleteChange}
            $borderless
            $background="transparent"
          />
          <Button
            iconLeft={Check}
            handleClick={handleSubmitChange}
            $borderless
            disabled={isError}
            $background="transparent"
          />
        </ButtonWrapper>
      )}
      </TextWrapper>
    </EditableTableTextContainer>
  );
};

export default EditableTableText;